/* eslint-disable @typescript-eslint/no-explicit-any */
import { Instant, LocalDateTime, ZoneId } from 'js-joda';
import { size, toNumber } from 'es-toolkit/compat';

import * as Service from 'types/service';
import { ActionsType } from '@tock/shared/utils/connect';
import { EventAction } from 'utils/analytics/amplitudeEventConstants';
import {
  SidePanelCRUDAction,
  SidePanelClickLocation,
  SidePanelViewMode,
} from 'actions/ReservationsSidePanelActions';
import { amplitudeSendEvent } from 'utils/analytics/amplitude';
import { convertFullNameToFirstLast, isPartyWalkIn } from 'utils/partyUtils';
import { dashboard } from 'types';
import { formatReservationTime } from 'utils/timeUtils';
import { safeToNumber } from '@tock/coretypes';
import PartyStates from 'constants/partyStateConstants';

export interface WalkInFormData {
  createGuestProfile: boolean;
  email: string;
  name: string;
  search: string;
  sendWalkinConfirmationText: boolean;
  needs: string[];
  partySize: number;
  phone: string;
  phoneCountryCode: string;
  quotedTime: string;
  turnTimeMins: number;
  reservationTime: string;
  selectedPatron: dashboard.GuestProfileSuggestion | null;
  shortNotes: string;
  state: string;
  secondaryState: string;
  newGuestProfile?: dashboard.GuestProfile;
}

export const DEFAULT_WALK_IN_NAME = 'Walk-in';

function createWalkInDescriptionShared(
  formData: WalkInFormData,
  timeZone: string,
  selectedTableIds: number[],
  isQuotedWalkIn: boolean,
  textMessages: dashboard.TextMessage[]
): Service.ElmWalkInPartyDescription {
  const { selectedPatron, newGuestProfile } = formData;
  const { firstName, lastName } = convertFullNameToFirstLast(formData.name, {
    reverse: true,
  });
  const partyId = -(Math.floor(Math.random() * (Number.MAX_SAFE_INTEGER - 1)) + 1);

  if (typeof formData.phoneCountryCode !== 'string') {
    window.LOGGER?.error(
      `WalkIn created where country code receive not a string, received ${JSON.stringify(
        formData.phoneCountryCode
      )}`
    );
    window.LOGGER?.error(new Error().stack);
  }

  const walkInPartyDescription: Service.ElmWalkInPartyDescription = {
    // We pick a random number for the ID as a hacky way to get a globally unique ID for walk-ins.
    // It will break the same number is picked twice (either by the same client or by two different
    // clients) for the same service.
    id: partyId,
    firstName,
    lastName,
    numPeoplePresent: toNumber(formData.partySize) || 2, // If something breaks, default to 2
    email: formData.email,
    partySize: formData.partySize,
    phone: formData.phone,
    phoneCountryCode: formData.phoneCountryCode,
    shortNotes: formData.shortNotes,
    tag: formData.needs,
    textMessage: textMessages,
    state: (formData.state as Service.PartyState) || PartyStates.expected,
    secondaryState: formData.secondaryState || '',
  };

  if (formData.turnTimeMins) {
    walkInPartyDescription.turnTimeOverrideMins = formData.turnTimeMins;
  }

  const noTableSelected = size(selectedTableIds) === 0;
  const quotedTimeSet = toNumber(formData.quotedTime) >= 0;
  const quotedTimeGreaterThanZero = toNumber(formData.quotedTime) > 0;

  if (isQuotedWalkIn && noTableSelected && quotedTimeGreaterThanZero) {
    walkInPartyDescription.expectedReservationTime = formatReservationTime(
      LocalDateTime.ofInstant(Instant.now(), ZoneId.of(timeZone)).plusMinutes(
        toNumber(formData.quotedTime)
      )
    );
  }

  if (isQuotedWalkIn && quotedTimeSet) {
    walkInPartyDescription.expectedReservationTime = '';
    walkInPartyDescription.quotedTimeMinutes = toNumber(formData.quotedTime);
    if (!noTableSelected) {
      walkInPartyDescription.quotedTimeMinutes = 0;
      walkInPartyDescription.seatedTime = Math.round(Instant.now().toEpochMilli() / 1000);
    }
  }

  if (!isQuotedWalkIn) {
    walkInPartyDescription.expectedReservationTime = formData.reservationTime;
    delete walkInPartyDescription.quotedTimeMinutes;
  }

  if (selectedPatron) {
    const { firstName, lastName } = convertFullNameToFirstLast(selectedPatron.name, {
      reverse: true,
    });
    walkInPartyDescription.firstName = firstName;
    walkInPartyDescription.lastName = lastName;
    walkInPartyDescription.patronId = safeToNumber(selectedPatron.patronId);
    walkInPartyDescription.email = selectedPatron.email;
    walkInPartyDescription.phone = walkInPartyDescription.phone || selectedPatron.beautifiedPhone;
  }

  // newGuestProfile variable is the form value for `Add to guest directory`.
  // This toggle shows when a name is added but not linked to a profile.
  // By default `newGuestProfile` is true even when an existing patron is selected
  if (newGuestProfile && (formData.createGuestProfile || selectedPatron)) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    walkInPartyDescription.patronId = safeToNumber(newGuestProfile.patron!.id);
  }

  return walkInPartyDescription;
}

export function createWalkInDescription(
  formData: WalkInFormData,
  selectedParty: Service.Party | undefined,
  timeZone: string,
  selectedTableIds: number[],
  isQuotedWalkIn: boolean
): Service.ElmWalkInPartyDescription {
  if (isPartyWalkIn(selectedParty)) {
    const overrides: any = {
      id: selectedParty.id,
      state: selectedParty.state,
    };

    if (selectedParty.arrivalTime) {
      overrides.arrivalTime = selectedParty.arrivalTime;
    }

    if (selectedParty.seatedTime) {
      overrides.seatedTime = selectedParty.seatedTime;
    }
    if (selectedParty.leftTime) {
      overrides.leftTime = selectedParty.leftTime;
    }
    return createWalkInDescriptionForUpdate(
      formData,
      overrides,
      timeZone,
      selectedTableIds,
      isQuotedWalkIn,
      selectedParty.textMessage
    );
  } else {
    return createWalkInDescriptionForCreate(formData, timeZone, selectedTableIds, isQuotedWalkIn);
  }
}

function createWalkInDescriptionForCreate(
  formData: WalkInFormData,
  timeZone: string,
  selectedTableIds: number[],
  isQuotedWalkIn: boolean
): Service.ElmWalkInPartyDescription {
  const walkInPartyDescription = createWalkInDescriptionShared(
    formData,
    timeZone,
    selectedTableIds,
    isQuotedWalkIn,
    [] // there are no text messages for a new party
  );
  // arrivalTime is optional in elm, but needs to not be set, not undefined in order to be decoded
  walkInPartyDescription.walkInCreatedTimeSec = walkInPartyDescription.arrivalTime = Math.round(
    Instant.now().toEpochMilli() / 1000
  );
  return walkInPartyDescription;
}

function createWalkInDescriptionForUpdate(
  formData: WalkInFormData,
  overrides: object,
  timeZone: string,
  selectedTableIds: number[],
  isQuotedWalkIn: boolean,
  textMessages: dashboard.TextMessage[]
): Service.ElmWalkInPartyDescription {
  const walkInPartyDescription = createWalkInDescriptionShared(
    formData,
    timeZone,
    selectedTableIds,
    isQuotedWalkIn,
    textMessages
  );
  return {
    ...walkInPartyDescription,
    ...overrides,
  };
}

export function getExpectedReservationTime(party: Service.Party) {
  const arrivalTimeInstant = party.arrivalTime
    ? LocalDateTime.ofInstant(Instant.ofEpochSecond(party.arrivalTime, 0))
    : LocalDateTime.now();

  return (
    party.expectedReservationTime ||
    formatReservationTime(
      arrivalTimeInstant.plusMinutes(party.quotedTimeMinutes ? Number(party.quotedTimeMinutes) : 0)
    )
  );
}

export function openWalkinSidePanel(
  storeActions: ActionsType,
  flowExecutionId: string,
  action?: SidePanelCRUDAction,
  viewMode?: SidePanelViewMode,
  location?: SidePanelClickLocation
) {
  if (action === 'NEW') {
    amplitudeSendEvent(EventAction.WALK_IN_PANEL_NEW, {
      actionLocation: location,
      tableViewMode: viewMode,
      flowExecutionId,
    });
  } else {
    amplitudeSendEvent(EventAction.WALK_IN_PANEL_EDIT, {
      actionLocation: location,
      tableViewMode: viewMode,
      flowExecutionId,
    });
  }

  storeActions.reservationsSidePanel.openSidePanelWithPayload({
    type: 'WALKIN_SIDE_PANEL',
    action,
    viewMode,
    location,
  });
}

export function getWalkinPartyColorClassNameFromStateDescription(
  stateDescription: Service.StateDescription | undefined,
  isWalkinSelected: boolean,
  defaultColorClassName: string
): string {
  if (stateDescription?.borderClassName) {
    if (!isWalkinSelected) {
      return stateDescription.className;
    }

    return [defaultColorClassName, stateDescription.borderClassName].join(' ');
  }

  return defaultColorClassName;
}
